"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../../lib";
import type { MediaPlayerSeekSegment } from "../../media-player/media-player-seek-bar";
import type { MediaPlayerTheme } from "../../media-player/media-player-theme";
import {
	resolveCardPlayerTheme,
	TOPIC_CARD_PLAYER_THEME,
} from "../../media-player/media-player-theme";
import { MediaPlayerTrackGlow } from "../../media-player/media-player-track-glow";
import { MediaPlayerTrackParticles } from "../../media-player/media-player-track-particles";
import {
	MEDIA_PLAYER_SEGMENT_DIVIDER_CLASSNAME,
	resolveMediaPlayerContinuationLockStartPercent,
	resolveMediaPlayerScrubClampPercent,
	resolveMediaPlayerScrubHitWidthPercent,
	useMediaPlayerTrackSegmentTooltip,
} from "../../media-player/media-player-track-segment-tooltip";
import { MediaPlayerTrackSegmentTooltipLabel } from "../../media-player/media-player-track-segment-tooltip-label";
import { useMediaPlayerScrub } from "../../media-player/use-media-player-scrub";
import {
	MEDIA_PLAYER_SEEK_PIN_HIDE_DELAY_MS,
	useMediaPlayerSeekPin,
} from "../../media-player/use-media-player-seek-pin";
import { MediaPlayerSeekPinOverlay } from "../media-player-seek-pin-overlay";
import type {
	InteractiveAudioSegmentRegion,
	InteractiveMediaBarsLayout,
} from "../interactive-audio-player/types";

export interface InteractiveMediaBarsTrackProps {
	theme?: MediaPlayerTheme;
	value: number;
	maxSeekPercent: number;
	continuationPercent?: number;
	furthestConsumedPercent?: number;
	/** When set, preferred over recomputing clamp from continuation/maxSeek. */
	scrubClampPercent?: number;
	roundedFull?: boolean;
	showThumb?: boolean;
	/**
	 * White circle + caret pin at the playhead (narrow edge scrub).
	 * Fades in while scrubbing or hovering; fades out after {@link seekPinHideDelayMs}.
	 */
	showSeekPin?: boolean;
	/** Keep the pin visible as the persistent playhead (not only while scrubbing). */
	seekPinAlwaysVisible?: boolean;
	/** Delay after scrub release before the seek pin fades out. */
	seekPinHideDelayMs?: number;
	thumbDiameterPx?: number;
	thumbClassName?: string;
	segments?: MediaPlayerSeekSegment[];
	interactive: boolean;
	/** Soft aurora glow behind the track (playhead-masked; no cursor follow — D12). */
	trackGlow?: boolean;
	/** Ambient particles behind the bar (shared with waveform chrome; audio keeps waveform). */
	showAmbientParticles?: boolean;
	ambientParticleColor?: string;
	onPause: () => void;
	onSeekCommit: (percent: number) => void;
	onSeekPreview?: (percent: number) => void;
	onSeekRejected?: () => void;
	className?: string;
	"aria-label"?: string;
	seekEpoch?: number;
}

/** Pill rail height; aurora sits in a taller hit area when `trackGlow` is on. */
export const MEDIA_PLAYER_CARD_SEEK_TRACK_HEIGHT_PX = 10;

/** Topic text/audio scrub thumb — larger than the 10px rail, centered on playhead. */
export const TOPIC_LEARN_SCRUB_THUMB_DIAMETER_PX = 24;

const TOPIC_CARD_SEEK_THUMB_CLASSNAME =
	"border border-black/10 bg-white shadow-[0_1px_4px_rgba(0,0,0,0.22)]";

const PROGRESS_MASKED_SEGMENT_DIVIDER_CLASSNAME =
	"bg-black/20 dark:bg-black/20";

export function resolveInteractiveMediaBarsTheme(input: {
	barsLayout?: InteractiveMediaBarsLayout;
	trackGlowColor1?: string;
	trackGlowColor2?: string;
	ambientParticleColor?: string;
}): MediaPlayerTheme {
	const base = resolveCardPlayerTheme(input.barsLayout === "narrow");
	return {
		...base,
		...(input.trackGlowColor1 != null
			? { trackGlowColor1: input.trackGlowColor1 }
			: {}),
		...(input.trackGlowColor2 != null
			? { trackGlowColor2: input.trackGlowColor2 }
			: {}),
		...(input.ambientParticleColor != null
			? { ambientParticleColor: input.ambientParticleColor }
			: {}),
	};
}

export function mapInteractiveAudioSegmentsToSeekSegments(
	segments: readonly InteractiveAudioSegmentRegion[],
): MediaPlayerSeekSegment[] {
	return segments.map((segment, index) => ({
		percent: segment.startPercent,
		label: segment.label ?? segment.id,
		cardIndex: segment.cardIndex ?? index,
	}));
}

function resolveSegmentDividerLeftPx(
	percent: number,
	layoutWidthPx: number,
): number {
	return (percent / 100) * layoutWidthPx - 1.5;
}

function renderMediaPlayerSegmentDividers(
	segments: readonly MediaPlayerSeekSegment[],
	className: string,
	keyPrefix: string,
	layoutWidthPx: number,
) {
	return segments.map((segment, index) => {
		const leftPercent = Math.max(0, Math.min(100, segment.percent));
		if (leftPercent <= 0) {
			return null;
		}
		const leftStyle =
			layoutWidthPx > 0
				? {
						left: `${resolveSegmentDividerLeftPx(leftPercent, layoutWidthPx)}px`,
					}
				: {
						left: `calc(${leftPercent}% - 1.5px)`,
					};
		return (
			<div
				key={`${keyPrefix}-${segment.cardIndex ?? index}-${segment.label}`}
				className={cn(
					"pointer-events-none absolute top-0 bottom-0",
					MEDIA_PLAYER_SEGMENT_DIVIDER_CLASSNAME,
					className,
				)}
				style={leftStyle}
				aria-hidden
			/>
		);
	});
}

export function InteractiveMediaBarsTrack({
	theme = TOPIC_CARD_PLAYER_THEME,
	value,
	maxSeekPercent,
	continuationPercent,
	furthestConsumedPercent = maxSeekPercent,
	scrubClampPercent: scrubClampPercentProp,
	roundedFull = true,
	showThumb = false,
	showSeekPin = false,
	seekPinAlwaysVisible = false,
	seekPinHideDelayMs = MEDIA_PLAYER_SEEK_PIN_HIDE_DELAY_MS,
	thumbDiameterPx = TOPIC_LEARN_SCRUB_THUMB_DIAMETER_PX,
	thumbClassName,
	segments = [],
	interactive,
	trackGlow = true,
	showAmbientParticles = false,
	ambientParticleColor,
	onPause,
	onSeekCommit,
	onSeekPreview,
	onSeekRejected,
	className,
	"aria-label": ariaLabel = "Card autoplay progress",
	seekEpoch = 0,
}: InteractiveMediaBarsTrackProps) {
	const trackRef = useRef<HTMLDivElement>(null);
	const [trackWidthPx, setTrackWidthPx] = useState(0);
	const [isTrackHovering, setIsTrackHovering] = useState(false);
	const resolvedTheme =
		ambientParticleColor != null
			? { ...theme, ambientParticleColor }
			: theme;

	useLayoutEffect(() => {
		const element = trackRef.current;
		if (!element) {
			return;
		}
		const updateWidth = () => {
			setTrackWidthPx(element.getBoundingClientRect().width);
		};
		updateWidth();
		const resizeObserver = new ResizeObserver(updateWidth);
		resizeObserver.observe(element);
		return () => resizeObserver.disconnect();
	}, []);

	const scrubHitWidthPercent = resolveMediaPlayerScrubHitWidthPercent(
		maxSeekPercent,
		continuationPercent,
	);
	const scrubClampPercent =
		scrubClampPercentProp ??
		resolveMediaPlayerScrubClampPercent(
			maxSeekPercent,
			continuationPercent,
			furthestConsumedPercent,
		);
	const {
		playheadPercent,
		isScrubbing,
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerLeave,
		onPointerCancel,
	} = useMediaPlayerScrub({
		progressPercent: value,
		maxSeekPercent,
		scrubClampPercent,
		interactive,
		seekEpoch,
		trackBoundsRef: trackRef,
		onPause,
		onSeekCommit,
		onSeekPreview,
		onSeekRejected,
	});
	const { pinVisible, pinEnterMode, showLockIcon } = useMediaPlayerSeekPin({
		isScrubbing,
		isHovering: interactive && isTrackHovering,
		playheadPercent,
		scrubClampPercent,
		hideDelayMs: seekPinHideDelayMs,
		alwaysVisible: seekPinAlwaysVisible,
		seekEpoch,
	});
	const {
		trackTooltipLabel,
		trackTooltipAnchorCenterPercent,
		mergePointerHandlers,
	} = useMediaPlayerTrackSegmentTooltip(segments, trackRef);
	const trackPointerHandlers = mergePointerHandlers({
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerLeave,
		onPointerCancel,
	});
	const trackShape = roundedFull ? "rounded-full" : "rounded-none";
	const lockedStart = Math.max(furthestConsumedPercent, maxSeekPercent);
	const continuationLockStartPercent =
		resolveMediaPlayerContinuationLockStartPercent(
			maxSeekPercent,
			furthestConsumedPercent,
			continuationPercent,
		);
	const interactiveScrubWidthPercent =
		typeof continuationPercent === "number"
			? continuationLockStartPercent
			: scrubHitWidthPercent;
	const continuationFillPercent =
		typeof continuationPercent === "number"
			? Math.max(0, Math.min(100, continuationPercent))
			: null;
	const scrubHitActive = interactive;
	const thumbHalfPx = thumbDiameterPx / 2;
	const playheadMarkerActive = showThumb || showSeekPin;
	const scrubHitHeightPx = playheadMarkerActive
		? Math.max(thumbDiameterPx, showSeekPin ? 36 : thumbDiameterPx)
		: trackGlow
			? 44
			: MEDIA_PLAYER_CARD_SEEK_TRACK_HEIGHT_PX;

	return (
		<div
			className={cn(
				"relative z-[1] w-full",
				(trackGlow || showAmbientParticles) &&
					"flex min-h-11 items-center overflow-visible py-2",
				showSeekPin && "overflow-visible",
				playheadMarkerActive && "overflow-visible",
				className,
			)}
			role="slider"
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(playheadPercent)}
			aria-label={ariaLabel}
			aria-disabled={!interactive}
			onPointerEnter={
				interactive
					? () => {
							setIsTrackHovering(true);
						}
					: undefined
			}
			onPointerLeave={
				interactive
					? () => {
							setIsTrackHovering(false);
						}
					: undefined
			}
		>
			{showAmbientParticles ? (
				<MediaPlayerTrackParticles theme={resolvedTheme} />
			) : null}
			<div
				ref={trackRef}
				className={cn(
					"relative z-[1] w-full bg-border",
					trackGlow || showSeekPin
						? "overflow-visible"
						: "overflow-hidden",
					trackShape,
					resolvedTheme.trackClassName,
				)}
				style={{ height: MEDIA_PLAYER_CARD_SEEK_TRACK_HEIGHT_PX }}
			>
				{trackGlow ? (
					<MediaPlayerTrackGlow
						playheadPercent={playheadPercent}
						thumbDiameterPx={
							playheadMarkerActive ? thumbDiameterPx : undefined
						}
						trackWidthPx={
							playheadMarkerActive ? trackWidthPx : undefined
						}
						className="-top-4 -bottom-4"
						color1={resolvedTheme.trackGlowColor1}
						color2={resolvedTheme.trackGlowColor2}
						enableMouseInteraction={false}
					/>
				) : null}
				<div
					className="pointer-events-none absolute inset-y-0 left-0 right-0 z-[1]"
					aria-hidden
				>
					{renderMediaPlayerSegmentDividers(
						segments,
						"bg-white",
						"segment",
						trackWidthPx,
					)}
				</div>
				{continuationFillPercent != null &&
				continuationFillPercent > 0 ? (
					<div
						className={cn(
							"pointer-events-none absolute inset-y-0 left-0 z-[1]",
							trackShape,
							resolvedTheme.seekBarContinuationClassName ||
								"bg-white/40 dark:bg-white/25",
						)}
						style={{ width: `${continuationFillPercent}%` }}
						aria-hidden
					/>
				) : null}
				{lockedStart < 100 ? (
					<div
						className={cn(
							"pointer-events-none absolute inset-y-0 right-0 z-[1]",
							trackShape,
							resolvedTheme.seekBarLockedZoneClassName ||
								"bg-black/5 dark:bg-white/5",
						)}
						style={{ left: `${lockedStart}%` }}
						aria-hidden
					/>
				) : null}
				<div
					className={cn(
						"pointer-events-none absolute inset-y-0 left-0 z-[2]",
						trackShape,
						resolvedTheme.indicatorClassName,
					)}
					style={{ width: `${playheadPercent}%` }}
				/>
				{playheadPercent > 0 ? (
					<div
						className="pointer-events-none absolute inset-y-0 left-0 z-[4] overflow-hidden"
						style={{ width: `${playheadPercent}%` }}
						aria-hidden
					>
						<div
							className="relative h-full"
							style={{
								width:
									trackWidthPx > 0
										? trackWidthPx
										: `calc(100% * 100 / ${playheadPercent})`,
							}}
						>
							{renderMediaPlayerSegmentDividers(
								segments,
								PROGRESS_MASKED_SEGMENT_DIVIDER_CLASSNAME,
								"progress-segment",
								trackWidthPx,
							)}
						</div>
					</div>
				) : null}
				{showThumb && !showSeekPin ? (
					<div
						className={cn(
							"pointer-events-none absolute top-1/2 z-[5] -translate-y-1/2 rounded-full",
							TOPIC_CARD_SEEK_THUMB_CLASSNAME,
							thumbClassName,
						)}
						style={{
							width: thumbDiameterPx,
							height: thumbDiameterPx,
							left: `clamp(0px, calc(${playheadPercent}% - ${thumbHalfPx}px), calc(100% - ${thumbDiameterPx}px))`,
						}}
						aria-hidden
					/>
				) : null}
				{showSeekPin ? (
					<MediaPlayerSeekPinOverlay
						pinVisible={pinVisible}
						pinEnterMode={pinEnterMode}
						showLockIcon={showLockIcon}
						playheadPercent={playheadPercent}
						interactive={interactive}
						onPointerDown={trackPointerHandlers.onPointerDown}
						onPointerMove={trackPointerHandlers.onPointerMove}
						onPointerUp={trackPointerHandlers.onPointerUp}
						onPointerLeave={trackPointerHandlers.onPointerLeave}
						onPointerCancel={trackPointerHandlers.onPointerCancel}
					/>
				) : null}
			</div>
			{scrubHitActive ? (
				<div
					className="pointer-events-none absolute left-0 right-0 z-[5]"
					style={{
						height: scrubHitHeightPx,
						top: "50%",
						transform: "translateY(-50%)",
					}}
				>
					{interactiveScrubWidthPercent > 0 ? (
						<div
							className={cn(
								"pointer-events-auto absolute inset-y-0 left-0 touch-none select-none",
								trackShape,
								"cursor-pointer",
							)}
							style={{
								width: `${interactiveScrubWidthPercent}%`,
								minWidth: "12px",
							}}
							onPointerDown={trackPointerHandlers.onPointerDown}
							onPointerMove={trackPointerHandlers.onPointerMove}
							onPointerUp={trackPointerHandlers.onPointerUp}
							onPointerLeave={trackPointerHandlers.onPointerLeave}
							onPointerCancel={
								trackPointerHandlers.onPointerCancel
							}
						/>
					) : null}
					{continuationLockStartPercent < 100 ? (
						<div
							className={cn(
								"pointer-events-auto absolute inset-y-0 right-0 cursor-not-allowed touch-none select-none",
								trackShape,
							)}
							style={{ left: `${continuationLockStartPercent}%` }}
							onPointerDown={(event) => {
								event.preventDefault();
								onSeekRejected?.();
							}}
						/>
					) : null}
				</div>
			) : null}
			{trackTooltipLabel != null &&
			trackTooltipAnchorCenterPercent != null ? (
				<MediaPlayerTrackSegmentTooltipLabel
					label={trackTooltipLabel}
					anchorCenterPercent={trackTooltipAnchorCenterPercent}
				/>
			) : null}
		</div>
	);
}
